import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {TranslationDomainService} from '../../../../services/translation-domain/translation-domain.service';
import {TranslationDomain} from '../../../../models/translation/translation-domain';
import {TranslationMessageService} from '../../../../services/translation-message/translation-message.service';
import {NgClass, NgOptimizedImage, ViewportScroller} from '@angular/common';
import {TranslationMessage} from '../../../../models/translation/translation-message';
import {LangService} from '../../../../services/language/lang.service';
import {InputSwitchComponent} from '../../../layout/input-switch/input-switch.component';
import {FormsModule} from '@angular/forms';
import {LoaderService} from '../../../../services/loader/loader.service';
import {TranslationMessageEditorComponent} from '../translation-message-editor/translation-message-editor.component';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {
  TranslationMessageListTranslateComponent
} from '../translation-message-list-translate/translation-message-list-translate.component';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {TranslationKnowledgeComponent} from './translation-knowledge/translation-knowledge.component';
import {SearchReplaceComponent} from './search-replace/search-replace.component';
import {Translation} from '../../../../types/translation';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';

@Component({
  selector: 'app-translation-message-list',
  imports: [
    RouterLink,
    NgOptimizedImage,
    InputSwitchComponent,
    FormsModule,
    TranslationMessageEditorComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgClass
  ],
  templateUrl: './translation-message-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './translation-message-list.component.scss'
})
@IsGranted(['TRANSLATION_VIEW'])
@MetaTitle('Translations')
export class TranslationMessageListComponent implements OnInit {

  private static readonly ANCHOR_OFFSET = 200;
  private static readonly ANCHOR_SAMPLE_DELAY = 100;
  private static readonly ANCHOR_MAX_WAIT = 3000;

  errorAnchor: string | null = null;
  translationDomain: TranslationDomain | null = null;
  displayedLocales: string[] = [];
  availableLocales: string[] = [];
  reviewedLocales: { [locale: string]: boolean } = {};
  keys: MessageKey[] = [];
  baseLocale: string | null = null;
  toLocales: string[] = [];

  constructor(
    private translationDomainService: TranslationDomainService,
    private translationMessageService: TranslationMessageService,
    private route: ActivatedRoute,
    private dialog: DialogService,
    private viewportScroller: ViewportScroller,
    private snackbar: SnackbarService,
    protected loader: LoaderService,
    protected langService: LangService
  ) {
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    this.errorAnchor = this.route.snapshot.queryParamMap.get('error');

    if (id) {
      this.loader.startLoading();
      this.translationDomainService.findOneById(id).subscribe((translationDomain) => {
        this.translationDomain = translationDomain;
        this.baseLocale = this.translationDomain.baseLocale;

        this.translationDomain.locales.forEach(domainLocale => {
          if (domainLocale.locale) {
            this.displayedLocales.push(domainLocale.locale);
            this.availableLocales.push(domainLocale.locale);
            this.reviewedLocales[domainLocale.locale] = domainLocale.reviewed;
          }
        });

        this.updateToLocales();

        this.sortLocales();

        this.refreshMessages();
      });
    }
  }

  openSearchReplaceModal(locale: string): void {
    const dataToReplace: Translation[] = [];
    for (const key of this.keys) {
      dataToReplace.push({
        key: key.key,
        text: this.getKeyMessage(key.locales, locale)?.text ?? ''
      });
    }

    this.dialog.openDialog(SearchReplaceComponent, {
      data: {
        description: this.translationDomain?.description ?? '',
        locale,
        data: dataToReplace,
      },
      class: 'modal-lg',
      backdrop: false
    }).subscribe((data: any) => {
      if (this.translationDomain?.id && data?.translations && data?.translations.length) {
        for (const translation of data.translations) {
          this.translationMessageService.writeMessage(
            this.translationDomain.id,
            locale,
            translation.key,
            translation.text
          ).subscribe(() => {
            this.updateKey(translation.key, locale, translation.text);
          });
        }
      }
    });
  }

  openKnowledgeModal(): void {
    this.dialog.openDialog(TranslationKnowledgeComponent).subscribe(() => {
    });
  }

  sortLocales(): void {
    this.availableLocales.sort();

    this.displayedLocales.sort((a, b) => {
      if (a === this.baseLocale) {
        return -1;
      }

      if (b === this.baseLocale) {
        return 1;
      }

      return a.localeCompare(b);
    });
  }

  refreshMessages(): void {
    this.loader.startLoading();

    this.keys = [];

    const params = {
      domainId: this.translationDomain?.id,
      locales: this.displayedLocales,
      sort: 'key'
    };

    this.translationMessageService.findAll(params).subscribe(messages => {
      const keys: any = {};

      messages.forEach(message => {
        const key = message.key;
        const locale = message.locale;
        if (!key || !locale) {
          return;
        }

        if (!(key in keys)) {
          keys[key] = [];
        }

        keys[key].push(message);
      });

      this.keys = [];
      Object.keys(keys).forEach(key => {
        this.keys.push({
          key: key,
          locales: keys[key]
        });
      });

      this.loader.stopLoading();

      if (this.errorAnchor) {
        this.scrollToAnchor(this.errorAnchor);
      }
    });
  }

  private scrollToAnchor(anchor: string): void {
    const fontSet: FontFaceSet | undefined = document.fonts;
    const fontsReady: Promise<unknown> = fontSet ? fontSet.ready : Promise.resolve();

    fontsReady.then(() => this.scrollToAnchorWhenStable(anchor, Date.now(), null));
  }

  private scrollToAnchorWhenStable(anchor: string, startedAt: number, previousTop: number | null): void {
    const element = document.getElementById(anchor);
    const top = element ? element.getBoundingClientRect().top + window.scrollY : null;
    const moved = top === null || previousTop === null || Math.abs(top - previousTop) > 1;
    const expired = Date.now() - startedAt > TranslationMessageListComponent.ANCHOR_MAX_WAIT;

    if (moved && !expired) {
      setTimeout(
        () => this.scrollToAnchorWhenStable(anchor, startedAt, top),
        TranslationMessageListComponent.ANCHOR_SAMPLE_DELAY
      );

      return;
    }

    this.viewportScroller.setOffset([0, TranslationMessageListComponent.ANCHOR_OFFSET]);
    this.viewportScroller.scrollToAnchor(anchor);
  }

  getKey(key: string): any {
    const result = this.keys.find((k: any) => {
      return k.key === key
    });

    return result ?? null;
  }

  getKeyMessage(messages: TranslationMessage[], locale: string): TranslationMessage | null {
    const result = messages.find((message: TranslationMessage) => message.locale === locale);

    return result ?? null;
  }

  reloadMessage(key: string, editor: TranslationMessageEditorComponent): void {
    editor.loading = true;

    this.translationMessageService.findAll({
      domainId: this.translationDomain?.id,
      key: key
    }).subscribe(reloadedMessages => {
      const currentKey = this.keys.find(k => k.key === key);
      if (currentKey) {
        currentKey.locales = reloadedMessages;
      }

      editor.loading = false;
      editor.edit();
    });
  }

  changeDisplayedLocale(locale: string, e: any): void {
    const checked = e.target.checked;

    if (checked) {
      this.displayedLocales.push(locale);
    } else {
      const i = this.displayedLocales.indexOf(locale);
      if (i >= 0) {
        this.displayedLocales.splice(i, 1);
      }
    }

    this.sortLocales();
  }

  isDisplayedLocale(locale: string): boolean {
    return this.displayedLocales.includes(locale);
  }

  updateKey(key: string, locale: string, text: string): void {
    const k = this.getKey(key);
    if (text) {
      const result = k.locales.find((message: TranslationMessage) => {
        return message.locale === locale;
      });

      if (result) {
        result.text = text;
      } else {
        const message = new TranslationMessage();
        message.key = key;
        message.locale = locale;
        message.text = text;
        k.locales.push(message);
      }
    } else {
      const i = k.locales.findIndex((message: TranslationMessage) => {
        return message.locale === locale;
      });
      if (i >= 0) {
        k.locales.splice(i, 1);
      }
    }
  }

  updateMessage(key: string, locale: string, text: string, editor: TranslationMessageEditorComponent): void {
    if (this.translationDomain?.id) {
      editor.loading = true;

      this.translationMessageService.writeMessage(
        this.translationDomain.id,
        locale,
        key,
        text
      ).subscribe(() => {
        this.updateKey(key, locale, text);

        editor.loading = false;
      });
    }
  }

  getColumnsWidth(): string {
    return Math.round(100 / this.displayedLocales.length) + '%';
  }

  getMissingTranslations(locale: string): number {
    let total = 0;
    let translated = 0;

    this.keys.forEach((key: any) => {
      total++;
      key.locales.forEach((message: TranslationMessage) => {
        if (message.locale === locale) {
          translated++;
        }
      });
    });

    return total - translated;
  }

  getPreviewUrl(locale: string): string | null {
    if (!this.translationDomain?.locales) {
      return null;
    }

    const l = this.translationDomain.locales.find((domainLocale) => {
      return domainLocale.locale === locale
    });

    if (!l) {
      return null;
    }

    return l.previewUrl + '?translationpreview';
  }

  translate(): void {
    this.dialog.openDialog(TranslationMessageListTranslateComponent, {
      data: {
        translationDomain: this.translationDomain,
      },
      backdrop: false,
    }).subscribe((data: any) => {
      if (this.translationDomain && data && data.targetLocale) {
        this.loader.startLoading();
        this.translationDomainService.translate(this.translationDomain, data.targetLocale, data.overwrite, data.botPromptId).subscribe(() => {
          this.loader.stopLoading();
          this.snackbar.success('Translation will be processed soon.');
        });
      }
    });
  }

  onReview(locale: string): void {
    if (this.translationDomain) {
      this.translationDomainService.reviewDomainLocale(this.translationDomain, locale, this.reviewedLocales[locale]).subscribe(() => {
        const status = this.reviewedLocales[locale] ? 'reviewed' : 'pending';
        this.snackbar.success(
          'Translations for "' + this.translationDomain?.domain +
          '" are flagged as ' + status + ' for the "' + locale + '" lang.'
        );
      });
    }
  }

  baseLocaleChanged(): void {
    this.updateToLocales();
    this.sortLocales();

    if (this.translationDomain?.id) {
      this.loader.startLoading();
      this.translationDomainService.update(this.translationDomain.id, {baseLocale: this.baseLocale}).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Base locale has been updated');
      });
    }
  }

  updateToLocales(): void {
    this.toLocales = this.availableLocales.filter(locale => locale !== this.baseLocale);
    this.toLocales.sort();
  }

  isHighlighted(key: string, locale: string): boolean {
    return this.errorAnchor === locale + '-' + key;
  }
}

interface MessageKey {
  key: string;
  locales: TranslationMessage[];
}
